import { Component, EventEmitter, Input, Output, HostListener, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, tap, catchError, takeUntil, Subject, take, BehaviorSubject, combineLatest, startWith } from 'rxjs';
import { of } from 'rxjs';
import { StorageService } from '../../../../shared/services/storage.service';

@Component({
  selector: 'app-search-input',
  standalone: false,
  templateUrl: './search-input.component.html',
  styleUrl: './search-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchInputComponent implements OnDestroy {
  filteredProducts: any[] = [];
  products: any[] = [];
  protected variants: any = {};
  protected selectedVariants: any = {};
  isSearching = false;
  searchError = false;

  private destroy$ = new Subject<void>();
  private variantsCache = new Map<number, any>();
  private products$ = new BehaviorSubject<any[]>([]);

  @Input() searchControl = new FormControl('');

  constructor(
    private readonly productsService: StorageService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) { }
  ngOnInit(): void {
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.isSearching = true;
          this.searchError = false;
          this.cdr.markForCheck();
        }),
        switchMap((query) => {
          const searchQuery = (query || '').trim();

          if (!searchQuery || searchQuery.length < 2) {
            this.isSearching = false;
            return of({ data: [] });
          }

          // Clean logs and technical prefixes
          let cleaned = searchQuery.replace(/\[Search\].*?\s/gi, '')
            .replace(/search-input\.component\.ts:\d+/gi, '')
            .replace(/\s+/g, ' ')
            .trim();

          // STAGE 1: Check Reference (Unique identifier)
          return this.productsService.getProducts(1, 10, '', { reference: cleaned }).pipe(
            switchMap(rRef => {
              if (rRef?.data?.length > 0) return of(rRef);

              // STAGE 2: If query looks like a technical title "Name - Specs"
              if (cleaned.includes(' - ')) {
                const parts = cleaned.split(' - ');
                const coreName = parts[0].trim();
                const withOneSpec = parts.length > 1 ? `${parts[0]} - ${parts[1]}`.trim() : coreName;

                // Try with one spec first
                return this.productsService.getProducts(1, 10, '', { label: withOneSpec }).pipe(
                  switchMap(rSpec => {
                    if (rSpec?.data?.length > 0 && rSpec.data.length < 5) return of(rSpec);

                    // Then try core name
                    return this.productsService.getProducts(1, 10, '', { label: coreName }).pipe(
                      switchMap(rCore => {
                        if (rCore?.data?.length > 0) return of(rCore);

                        // Fallback to full string
                        return this.productsService.getProducts(1, 10, '', { label: cleaned });
                      })
                    );
                  })
                );
              }

              // STAGE 3: Standard Title search
              return this.productsService.getProducts(1, 10, '', { label: cleaned }).pipe(
                switchMap(rLabel => {
                  if (rLabel?.data?.length > 0) return of(rLabel);

                  // STAGE 4: Word-based fallback
                  const words = cleaned.split(/[\s\-\,]+/).filter(w => w.length > 2);
                  if (words.length > 2) {
                    const partialMatch = words.slice(0, 3).join(' ');
                    return this.productsService.getProducts(1, 10, '', { label: partialMatch });
                  }

                  return of(rLabel);
                })
              );
            }),
            catchError(() => {
              this.searchError = true;
              return of({ data: [] });
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response: any) => {
        this.filteredProducts = this.processSearchResults(response?.data ?? []);
        this.isSearching = false;
        this.cdr.markForCheck();
      });
  }

  private processSearchResults(products: any[]): any[] {
    return products.map(product => {
      if (product.variants && product.variants.length > 0) {
        // Use cached variants if available
        const cacheKey = product.id;
        if (this.variantsCache.has(cacheKey)) {
          product.availableVariants = this.variantsCache.get(cacheKey);
        } else {
          const variants = this.processVariants(product.variants);
          product.availableVariants = Object.values(variants);
          this.variantsCache.set(cacheKey, product.availableVariants);
        }
      }
      return product;
    });
  }

  private processVariants(variants: any[]): any {
    const processedVariants: any = {};

    variants.forEach((variant: any) => {
      if (!processedVariants[variant?.variant?.id]) {
        processedVariants[variant?.variant?.id] = {
          variant: variant?.variant,
          values: [variant],
        };
      } else {
        processedVariants[variant?.variant?.id].values.push(variant);
      }
    });

    return processedVariants;
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.filteredProducts = [];
    this.isSearching = false;
    this.searchError = false;
    this.cdr.markForCheck();
  }

  onProductClick(product: any): void {
    // Clear the search when a product is clicked
    this.clearSearch();
  }

  onViewAllResults(searchQuery: string): void {
    // Clear the search when navigating to view all results
    this.clearSearch();
  }

  /**
   * Handle browse categories event from results component.
   * Clears the search so the dropdown closes.
   */
  onBrowseCategories(): void {
    this.clearSearch();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Define search-related selectors
    const searchSelectors = [
      '.search-container-wrapper',
      '.search-results-container',
      'app-search-results',
      '.search-input-container'
    ];

    // Check if the click is inside any search-related element
    const clickedInsideSearch = searchSelectors.some(selector => target.closest(selector));

    // If clicked outside search area and there's a search value with results, clear it
    if (!clickedInsideSearch && this.searchControl.value && this.filteredProducts.length > 0) {
      this.clearSearch();
    }
  }


}
